import type * as T from "../types";
import type { components } from "./schema";
import { accountGroup } from "./setup";

type Schemas = components["schemas"];
export type GeneralLedgerWire = Schemas["GeneralLedgerResponse"];
export type LedgerDocumentsWire = Schemas["LedgerDocumentMatch"][];
export type TrialBalanceAsOnWire = Schemas["TrialBalanceAsOn"];
export type CashFlowWire = Schemas["CashFlow"];
export type AgeingWire = Schemas["AgeingReportResponse"];
export type VatReturnWire = Schemas["VatReturnResponse"];
export type WithholdingWire = Schemas["WithholdingRegister"];
export type WithheldByCustomersWire = Schemas["CustomerWithholding"][];

function money(value: unknown): T.Money {
  const text = String(value);
  const negative = text.startsWith("-");
  const [whole = "0", fraction = ""] = (negative ? text.slice(1) : text).split(".");
  return `${negative ? "-" : ""}${whole}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}

export function toGeneralLedger({ ledger, parties }: GeneralLedgerWire): T.GeneralLedger {
  const names = new Map(parties.map((party) => [party.id, party.name]));
  return {
    accountId: ledger.accountId,
    code: ledger.code,
    name: ledger.name,
    from: ledger.from,
    to: ledger.to,
    opening: money(ledger.opening),
    closing: money(ledger.closing),
    totalDebit: money(ledger.totalDebit),
    totalCredit: money(ledger.totalCredit),
    allLines: Number(ledger.allLines),
    matching: Number(ledger.matching),
    lines: ledger.lines.map((line) => ({
      journalId: line.journalId,
      date: line.date,
      documentType: line.documentType,
      number: line.documentNumber,
      reference: line.reference ?? null,
      partyName: line.partyId ? (names.get(line.partyId) ?? null) : null,
      debit: money(line.debit),
      credit: money(line.credit),
      balance: money(line.balance),
    })),
    parties: parties.map((party) => ({ id: party.id, name: party.name })),
  };
}

export function toLedgerDocuments(wire: LedgerDocumentsWire): T.LedgerDocumentMatch[] {
  return wire.map((match) => ({
    journalId: match.journalId,
    number: match.documentNumber,
    date: match.postingDate,
    narration: match.narration ?? null,
    amount: money(match.amount),
  }));
}

export function toTrialBalanceAsOn(wire: TrialBalanceAsOnWire): T.TrialBalanceAsOn {
  return {
    asOn: wire.asOn,
    fiscalYearStart: wire.fiscalYearStart,
    rows: wire.rows.map((row) => ({
      accountId: row.accountId,
      code: row.code,
      name: row.name,
      group: accountGroup(row.type),
      debit: money(row.debit),
      credit: money(row.credit),
    })),
    totalDebit: money(wire.totalDebit),
    totalCredit: money(wire.totalCredit),
  };
}

const plain = (value: unknown) => String(Number(value));

export function toCashFlow(wire: CashFlowWire): T.CashFlowReport {
  const lines = (rows: CashFlowWire["operating"]): T.CashFlowLine[] =>
    rows.map((row) => ({
      accountId: row.accountId,
      code: row.code,
      name: row.name,
      amount: money(row.amount),
    }));
  return {
    from: wire.from,
    to: wire.to,
    netResult: money(wire.netResult),
    operating: lines(wire.operating),
    investing: lines(wire.investing),
    financing: lines(wire.financing),
    openingCash: money(wire.openingCash),
    closingCash: money(wire.closingCash),
  };
}

export type ReceivableAnalysisWire = Schemas["ReceivableAnalysis"];
export type PayableAnalysisWire = Schemas["PayableAnalysis"];
export type OpenItemsWire = Schemas["OpenItems"];

export function toReceivableAnalysis(wire: ReceivableAnalysisWire): T.ReceivableAnalysis {
  return {
    asAt: wire.asAt,
    total: money(wire.total),
    bucketDays: wire.bucketDays.map(Number),
    bucketTotals: wire.bucketTotals.map(money),
    averageDaysToPay: wire.averageDaysToPay == null ? null : Number(wire.averageDaysToPay),
    overdueInvoices: Number(wire.overdueInvoices),
    customers: wire.customers.map((customer) => ({
      id: customer.id,
      name: customer.name ?? null,
      paymentTermsDays: customer.paymentTermsDays == null ? "" : String(customer.paymentTermsDays),
      creditLimit: customer.creditLimit == null ? null : money(customer.creditLimit),
      outstanding: money(customer.outstanding),
      daysPastDue: customer.daysPastDue == null ? null : Number(customer.daysPastDue),
    })),
  };
}

export function toPayableAnalysis(wire: PayableAnalysisWire): T.PayableAnalysis {
  return {
    asAt: wire.asAt,
    total: money(wire.total),
    bucketDays: wire.bucketDays.map(Number),
    bucketTotals: wire.bucketTotals.map(money),
    suppliersWithOpenBills: Number(wire.suppliersWithOpenBills),
    overdueBills: Number(wire.overdueBills),
    dueWithinWeek: money(wire.dueWithinWeek),
    billsDueWithinWeek: Number(wire.billsDueWithinWeek),
    toDeduct: money(wire.toDeduct),
    withheldThisYear: money(wire.withheldThisYear),
    suppliers: wire.suppliers.map((supplier) => ({
      id: supplier.id,
      name: supplier.name,
      category: supplier.category,
      ratePct: String(Number(supplier.ratePercent)),
      openBills: money(supplier.openBills),
      toDeduct: money(supplier.toDeduct),
    })),
  };
}

export function toOpenItems(wire: OpenItemsWire): T.OpenItems {
  return {
    total: Number(wire.total),
    items: wire.items.map((item) => ({
      id: item.id,
      number: item.number ?? null,
      partyId: item.partyId,
      partyName: item.partyName,
      date: item.date,
      due: item.due,
      open: money(item.open),
    })),
  };
}

export function toAgeing(wire: AgeingWire): T.AgeingReport {
  return {
    asAt: wire.asAt,
    bucketDays: wire.bucketDays.map(Number),
    rows: wire.rows.map((row) => ({
      partyId: row.partyId,
      partyName: row.partyName ?? null,
      buckets: row.buckets.map(money),
      total: money(row.total),
    })),
    bucketTotals: wire.bucketTotals.map(money),
    total: money(wire.total),
  };
}

const VAT_CATEGORY: Record<Schemas["VatCategory"], string> = {
  standard: "Standard rate",
  zeroRated: "Zero-rated",
  exempt: "Exempt",
  reverseCharge: "Reverse charge",
  intraCommunity: "Intra-community",
  freeExport: "Export",
  outsideScope: "Outside scope",
};

export function toVatReturn(wire: VatReturnWire): T.VatReturn {
  return {
    from: wire.from,
    to: wire.to,
    output: wire.output.map((row) => ({
      category: VAT_CATEGORY[row.category],
      ratePct: row.rate === null ? null : plain(row.rate),
      taxable: money(row.taxable),
      tax: money(row.tax),
      documents: Number(row.documents),
    })),
    input: wire.input.map((row) => ({
      recoverable: row.recoverable,
      ratePct: plain(row.rate),
      taxable: money(row.taxable),
      tax: money(row.tax),
      documents: Number(row.documents),
    })),
    outputTax: money(wire.outputTax),
    recoverableInputTax: money(wire.recoverableInputTax),
    creditCarried: money(wire.creditCarried),
    payable: money(wire.payable),
  };
}

export function toWithholding(
  wire: WithholdingWire,
  customers: WithheldByCustomersWire,
): T.WithholdingReport {
  return {
    withheld: wire.rows.map((row) => ({
      documentId: row.documentId,
      number: row.number,
      date: row.billedOn,
      supplierName: row.supplierName,
      supplierTaxId: row.supplierTaxId ?? null,
      code: row.code,
      ratePct: plain(row.rate),
      base: money(row.paidAmount),
      amount: money(row.withheld),
    })),
    totalBase: money(wire.totalPaid),
    totalWithheld: money(wire.totalWithheld),
    byCustomers: customers.map((row) => ({
      receiptId: row.receiptId,
      number: row.number,
      date: row.receivedOn,
      customerName: row.customerName,
      code: row.code ?? null,
      amount: money(row.amount),
    })),
  };
}
